<template>
  <div
    v-show="isModalActive"
    class="back-in-stock-modal"
  >
    <overlay
      type="popup"
      direction="up"
      :is-active="isModalActive"
      parent-class="back-in-stock-modal"
      @close-overlay="toggleModal"
    >
      <template #body>
        <div
          class="back-in-stock-modal__wrapper"
          :class="[
            `is-${state}`,
            {
              'is-us': countryCode === 'US',
              'bloomreach-not-loaded': !bloomreachLoaded,
            }
          ]"
        >
          <header
            class="back-in-stock-modal__header overlay__header"
            js-overlay="headerElement"
          >
            <button
              class="back-in-stock-modal__close overlay__close"
              :aria-label="closeOverlayText"
              type="button"
              @click.prevent="toggleModal"
            >
              <icon-close />
            </button>
            <h2
              v-if="modalHeading"
              class="back-in-stock-modal__heading text-center"
              v-html="modalHeading"
            />

            <h3
              v-if="modalSubheading"
              class="back-in-stock-modal__subheading text-center"
              v-html="modalSubheading"
            />
          </header>

          <div
            class="overlay__content back-in-stock-modal__content"
            js-overlay="scrollElement"
          >
            <back-in-stock-form
              v-if="state === 'ready'"
              class="back-in-stock-modal__form"
              :product="product"
              :variant="variant"
              @on-bis-error="handleErrorBIS"
              @on-bis-success="handleSuccessBIS"
            />
            <div
              v-else-if="state === 'success'"
              ref="successState"
              class="back-in-stock-modal__success"
            >
              <h2
                v-if="bloomreachRecommendations.enabled && bloomreachRecommendations.title"
                class="back-in-stock-modal__ymal-title text-center"
                v-text="bloomreachRecommendations.title"
              />

              <bloomreach-recommendations
                v-if="bloomreachRecommendations.enabled"
                class="back-in-stock-modal__ymal"
                context="back-in-stock"
                :section-id="sectionId"
                :tracking-parameters="trackingParameters"
                :recommendation-id="bloomreachRecommendations.recommendationId"
                :recommendation-size="bloomreachRecommendations.recommendationSize"
                :initial-item-id="product?.id"
                :slides-per-view="{
                  mobile: 1.25,
                  tablet: 2.22,
                  desktop: 3,
                }"
                @recommendations-not-loaded="destroyComponent"
              />
            </div>
          </div>
        </div>
      </template>
    </overlay>
  </div>
</template>
<script>

/**
 * Component: Back In Stock Modal
 * -----------------------------------------------------------------------------
 * - Custom Back In Stock popup component.
 *
 * @param {Object} [trackingParameters] - Object of parameters, typically used for GTM, Bloomreach etc. tracking.
 * @param {Object} [bloomreachRecommendations] - Bloomreach recommendations settings object
 * @param {Boolean} [bloomreachRecommendations.enabled] - Whether the Bloomreach carousel should be enabled.
 * @param {String} [bloomreachRecommendations.title] - Title for the Bloomreach carousel.
 * @param {String} [bloomreachRecommendations.recommendationId] - Bloomreach recommendation ID.
 * @param {Number} [bloomreachRecommendations.recommendationSize] - Limit of Bloomreach products to show.
 */
import { v4 } from 'uuid'

import { mapState } from 'pinia'

import { useLocaleStore } from '~stores/locale'

import classes from '~/helpers/classes'
import { decodeHTMLEntities } from '~/helpers/strings'
import { importComponent } from '~/core/mint/helpers'
import { formatVariantTitle } from '~/helpers/format'

import IconClose from '~icons/misc/close.svg'

export default {
  name: 'BackInStockModal',

  components: {
    IconClose,
    Overlay: importComponent('overlay'),
    BackInStockForm: importComponent('back-in-stock-form'),
    BloomreachRecommendations: importComponent('bloomreach-recommendations'),
  },
  props: {
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    bloomreachRecommendations: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      state: 'hidden',
      product: null,
      variant: null,
      isModalActive: false,
      bloomreachLoaded: false,
    }
  },

  computed: {

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      countryCode: 'countryCode',
    }),

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeOverlayText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculate the modal heading text.
     * @returns {String}
     */
    modalHeading() {
      switch (this.state) {
        case 'ready':
          return mint.Strings('products.product.back_in_stock_modal.heading')

        case 'success':
          return mint.Strings('products.product.back_in_stock_modal.success_heading')

        case 'error':
          return mint.Strings('products.product.back_in_stock_modal.error_heading')

        default:
          return mint.Strings('products.product.back_in_stock_modal.heading')
      }
    },

    /**
     * Calculate the modal subheading text.
     * @returns {String}
     */
    modalSubheading() {
      const sizeTitle = formatVariantTitle(this.variant?.title)

      switch (this.state) {
        case 'ready':
          return this.product.comingSoon
            ? mint.Strings('products.product.back_in_stock_modal.subheading_for_coming_soon')
            : mint.Strings('products.product.back_in_stock_modal.subheading')

        case 'success':
          return mint.Strings('products.product.back_in_stock_modal.success_message', {
            replace: {
              variant: `<strong>${sizeTitle}</strong>`,
            },
          })

        case 'error':
          return decodeHTMLEntities(mint.Strings('products.product.back_in_stock_modal.error_message', {
            replace: {
              link: mint.routes.root,
            },
          }))

        default:
          return mint.Strings('products.product.back_in_stock_modal.subheading')
      }
    },

    /**
     * Calculate the random uniq ID, as the component used as a snippet.
     * @returns {String}
     */
    sectionId() {
      return v4()
    },
  },

  watch: {

    /**
     * Watching the `state`.
     * - once the form the form has been submitted successfully, we assume
     * the bloomreach recommendations should be displayed
     * - if the bloomreach component doesn't load, the corresponding handler will
     * change the `bloomreachLoaded` variable in order to update the styles
     */
    state(newValue) {
      if (newValue === 'success') {
        this.bloomreachLoaded = true
        return
      }
    },

    /**
     * Watching the `isModalActive` variable.
     * - Once popup was called - set state to `ready` to render modal's content.
     */
    isModalActive(newValue) {
      if (!newValue) {
        this.state = 'hidden'
        document.body.classList.remove(classes.locked)

        return
      }

      document.body.classList.add(classes.locked)
      this.state = 'ready'
    },
  },

  mounted() {
    this.setEventBusListeners()
  },

  methods: {

    /**
     * Toggle the visibility of the wishlist sidebar.
     */
    toggleModal() {
      this.isModalActive = !this.isModalActive
    },

    /**
     * Handle error state when `Back In Stock` request fails.
     */
    handleErrorBIS() {
      this.state = 'error'
    },

    /**
     * Handle state when `Back In Stock` and `Datitude` requests successfully sent.
     *
     * @param {Object} payload - Emitted data.
     * @param {Object} payload.response - Response data from Swym API.
     * @param {Object} payload.variant - Variant sent to Swym data from Swym API.
     */
    handleSuccessBIS(payload) {
      this.variant = payload?.variant
      this.state = 'success'
    },

    /**
     * Set Event Bus Listeners.
     */
    setEventBusListeners() {

      /**
       * Event triggered from the product component (either product card or form).
       * @param {Object} payload.product - Formatted Shopify product.
       * @param {Object} payload.variant - Current variant.
       */
      mint.EventBus.on('product:openBackInStock', (payload) => {
        const { product, variant } = payload

        if (!product || !variant) {
          mint.Log(
            'error',
            'Back In Stock triggered without variant or product',
            new Error('Back In Stock triggered without variant or product'),
            { product, variant },
          )

          return
        }

        this.product = product
        this.variant = variant

        this.toggleModal()
      })
    },

    /**
     * Remove the parent node when the products recommendations didn't load.
     */
    destroyComponent() {
      const parentSection = this.$refs.successState
      this.bloomreachLoaded = false

      parentSection?.remove()
    },
  },
}
</script>

<style src="./back-in-stock-modal.scss" lang="scss"></style>
